import * as React from 'react'
import { formatMoney } from './useChargebeeCheckout'
import {
    getVariantConfig,
    appProductName,
    isKeywordGraphApp,
} from '../../appVariant'
import { openBillingPortal } from './openBillingPortal'
import { asDate } from './subscriptionState'

// What the buyer sees the moment the subscription is created. The old hosted
// thank-you page (views/user/thank-you.ejs) is what people used to land on
// after paying, so its two jobs are kept: confirm the charge, and say what the
// subscription actually opens up.
// Docs live on each product's marketing site; keywordgraph.com hosts only the
// pages flagged `keywordgraph`.
const UNLOCKED = [
    {
        label: 'The web application, with unlimited graphs and AI chats',
        href: '/apps',
        keywordgraph: true,
    },
    { label: 'The API', docsPath: '/api', keywordgraph: true },
    {
        label: 'The n8n templates',
        docsPath: '/docs/n8n-workflow-templates-automations',
    },
    {
        label: 'The MCP server for Claude, ChatGPT and other clients',
        docsPath: '/mcp',
        keywordgraph: true,
    },
    { label: 'The Obsidian graph-view plugin', docsPath: '/obsidian-plugin' },
    { label: 'The browser extension', docsPath: '/extension', keywordgraph: true },
    {
        label: 'The LLM integrations',
        docsPath: '/docs/knowledge-graphs-for-llms',
    },
]

function unlockedFeatures() {
    const { brand } = getVariantConfig()
    return UNLOCKED.filter(item => !isKeywordGraphApp() || item.keywordgraph).map(
        ({ label, href, docsPath }) => ({
            label,
            href: docsPath ? `${brand.marketingDomain}${docsPath}` : href,
        })
    )
}

function linkTargetProps(href) {
    return href.startsWith('http')
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {}
}

export default function CheckoutSuccess({
    planLabel,
    mode,
    amount,
    currency,
    scheduledFor,
    trialEndsAt,
    returnTo,
    site,
}) {
    const productName = appProductName()
    const { billingPortalUrl } = getVariantConfig().brand
    const charged =
        mode === 'buy' && Number.isFinite(amount) && amount > 0
            ? formatMoney(amount, currency)
            : null
    // A scheduled change only switches at the end of the paid term; until then the
    // buyer keeps everything they have now.
    const scheduled = mode === 'change' && Boolean(scheduledFor)

    return (
        <div className="tw-flex tw-flex-col tw-gap-6" data-testid="upgrade-success">
            <div className="tw-flex tw-items-start tw-gap-4">
                <span
                    aria-hidden="true"
                    className="tw-mt-1 tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-success tw-text-xl tw-font-bold tw-text-success-foreground"
                >
                    ✓
                </span>
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <h1 className="tw-m-0 tw-text-2xl tw-font-semibold">
                        {scheduled
                            ? 'Plan change scheduled'
                            : mode === 'change'
                            ? 'Plan changed'
                            : 'Payment successful. Thank you!'}
                    </h1>
                    <p className="tw-m-0 tw-text-sm tw-text-muted-foreground">
                        {scheduled ? (
                            <span data-testid="upgrade-success-scheduled">
                                You’ll keep your current plan until{' '}
                                {asDate(scheduledFor)}. Your plan will then
                                change to {planLabel}.
                            </span>
                        ) : mode === 'change' ? (
                            <>
                                Your subscription is now on {planLabel}.
                                {trialEndsAt ? (
                                    <span data-testid="upgrade-success-trial">
                                        {' '}
                                        Your trial continues until{' '}
                                        {asDate(trialEndsAt)}. Nothing was
                                        charged today.
                                    </span>
                                ) : null}
                            </>
                        ) : (
                            <>
                                Your {planLabel} subscription is active.
                                {charged ? <> You paid {charged}.</> : null} The
                                invoice is in your billing portal.
                            </>
                        )}
                    </p>
                </div>
            </div>

            {scheduled ? null : (
                <div className="tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-card tw-p-5">
                    <p className="tw-m-0 tw-mb-2 tw-text-sm tw-font-semibold">
                        You now have full access to
                    </p>
                    <ul className="tw-m-0 tw-list-disc tw-pl-5 tw-text-sm tw-text-muted-foreground">
                        {unlockedFeatures().map(item => (
                            <li key={item.label}>
                                <a
                                    href={item.href}
                                    {...linkTargetProps(item.href)}
                                    className="tw-text-foreground tw-underline"
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <p className="tw-m-0 tw-mt-5 tw-border-0 tw-border-l-4 tw-border-solid tw-border-primary tw-pl-3 tw-text-base tw-font-medium tw-text-foreground">
                        {productName} and the research behind it are funded entirely
                        by the people who subscribe. Thank you for supporting it.
                    </p>
                </div>
            )}

            <div className="tw-flex tw-flex-wrap tw-gap-2">
                <a
                    href={returnTo}
                    data-testid="upgrade-success-continue"
                    className="tw-rounded-lg tw-bg-primary tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-primary-foreground tw-no-underline"
                >
                    Back to {productName}
                </a>
                <a
                    href="/subscription"
                    data-testid="upgrade-success-done"
                    className="tw-rounded-lg tw-border tw-border-solid tw-border-border tw-px-5 tw-py-2.5 tw-text-sm tw-text-foreground tw-no-underline"
                >
                    Your subscription
                </a>
                <button
                    type="button"
                    onClick={() =>
                        openBillingPortal({ site, fallbackUrl: billingPortalUrl })
                    }
                    data-testid="success-billing-portal"
                    className="tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-transparent tw-px-5 tw-py-2.5 tw-text-sm tw-text-foreground"
                >
                    Invoices and payment details
                </button>
            </div>
        </div>
    )
}
